import { Link, useNavigate } from "react-router-dom";
import { fileUrl } from "@/lib/api";
import { Star, MapPin, Heart, Eye, Pencil, Truck } from "lucide-react";
import { motion } from "framer-motion";
import { useFavorites } from "@/context/FavoritesContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const resolveImg = (img) => {
  if (!img) return "https://images.unsplash.com/photo-1581147036324-c1c89c2c8b5c?w=800&q=80";
  return img.startsWith("http") ? img : fileUrl(img);
};

export default function ListingCard({ listing, index = 0, owner = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fav = useFavorites();
  const saved = fav?.isFav(listing.id);

  const handleFav = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { toast.info("Sign in to save items for later"); return; }
    const res = await fav.toggle(listing.id);
    if (res?.favorited) toast.success("Saved to favorites");
    else if (res?.favorited === false) toast("Removed from favorites");
  };

  const handleEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/edit/${listing.id}`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: (index % 8) * 0.05 }}
    >
      <Link
        to={`/listing/${listing.id}`}
        data-testid={`listing-card-${listing.id}`}
        className="group block bg-white border border-black/10 rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-[0_12px_32px_rgba(0,0,0,0.08)] hover:border-black/20 transition-[transform,box-shadow,border-color] duration-300"
      >
        <div className="aspect-[4/3] overflow-hidden bg-[#F4F1EC] relative">
          <img
            src={resolveImg(listing.images?.[0])}
            alt={listing.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <button
            type="button"
            onClick={handleFav}
            data-testid={`fav-btn-${listing.id}`}
            aria-label={saved ? "Remove from favorites" : "Save for later"}
            className="absolute top-3 left-3 w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-sm hover:scale-110 active:scale-95 transition-transform"
          >
            <Heart className={`w-[18px] h-[18px] transition-colors ${saved ? "fill-[#FF5A00] text-[#FF5A00]" : "text-[#0A0A0A]"}`} />
          </button>
          <div className="absolute top-3 right-3 flex gap-1.5">
            {listing.is_sold && (
              <span className="bg-[#0A0A0A] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full">Sold</span>
            )}
            {(listing.listing_type === "rent" || listing.listing_type === "both") && (
              <span className="bg-white/90 backdrop-blur text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full">Rent</span>
            )}
            {(listing.listing_type === "sale" || listing.listing_type === "both") && (
              <span className="bg-[#FF5A00] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full">Sale</span>
            )}
          </div>
        </div>
        <div className="p-4">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#FF5A00]">{listing.category}</span>
            <div className="flex items-center gap-2">
              {listing.condition && <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{listing.condition}</span>}
              {listing.review_count > 0 && (
                <span className="flex items-center gap-1 text-xs font-semibold">
                  <Star className="w-3.5 h-3.5 fill-[#0A0A0A]" /> {listing.avg_rating}
                </span>
              )}
            </div>
          </div>
          <h3 className="font-display font-bold text-lg tracking-tight mt-1 leading-snug line-clamp-1">{listing.title}</h3>
          <p className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
            <MapPin className="w-3.5 h-3.5" /> {listing.location}
          </p>
          {listing.delivery_options?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {listing.delivery_options.map((o) => (
                <span key={o} className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide bg-[#FFF0E8] text-[#FF5A00] rounded-full px-2 py-0.5">
                  <Truck className="w-3 h-3" /> {o}
                </span>
              ))}
            </div>
          )}
          <div className="mt-3 flex items-baseline gap-3 flex-wrap">
            {(listing.listing_type === "rent" || listing.listing_type === "both") && (
              <span className="flex items-baseline gap-1">
                <span className="font-display text-xl font-bold">€{listing.price_per_day}</span>
                <span className="text-sm text-muted-foreground">/ day</span>
              </span>
            )}
            {(listing.listing_type === "sale" || listing.listing_type === "both") && (
              <span className="flex items-baseline gap-1">
                <span className="font-display text-xl font-bold text-[#FF5A00]">€{listing.sale_price}</span>
                <span className="text-sm text-muted-foreground">buy</span>
              </span>
            )}
          </div>
          {owner && (
            <div className="mt-3 flex items-center justify-between border-t border-black/5 pt-3">
              <span data-testid={`listing-views-${listing.id}`} className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <Eye className="w-3.5 h-3.5" /> {listing.views || 0} {listing.views === 1 ? "view" : "views"}
              </span>
              <button
                type="button"
                onClick={handleEdit}
                data-testid={`edit-listing-${listing.id}`}
                className="flex items-center gap-1 text-xs font-semibold text-[#0A0A0A] hover:text-[#FF5A00] transition-colors"
              >
                <Pencil className="w-3.5 h-3.5" /> Edit
              </button>
            </div>
          )}
        </div>
      </Link>
    </motion.div>
  );
}
